import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
} from "@mui/material";
import { styled } from "@mui/system";
import axios from "axios";
import { AppStateContext } from "../../App";
import baseURL from "../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DownloadIcon from "@mui/icons-material/Download";
import axiosInstance from "../../helpers/Axios";

const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 30vh;
  padding: 2rem;
  background-color: #f3f4f6;
  margin-top: 5rem;
`;

const FormContainer = styled(Box)`
  background-color: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 28rem;
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 0.75rem;
    margin: 1rem;
  }
`;

const StyledButton = styled(Button)`
  background-color: #e53e3e;
  &:hover {
    background-color: #c53030;
  }
`;

const DownloadButton = styled(Button)`
  background-color: #10b981;
  &:hover {
    background-color: #059669;
  }
`;

const LoaderContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  min-height: 150px;
  gap: 1.5rem;
`;

const Testformdeletepage = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const { access_token } = useContext(AppStateContext);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      alert("Please select an Excel file to upload.");
      return;
    }

    if (!access_token) {
      alert("Unauthorized! Please log in.");
      return;
    }

    setLoading(true);
    setOpen(true); // Open dialog immediately to show loading state

    try {
      const formData = new FormData();
      formData.append("excel_file", file);

      // First make a request to get the JSON response data
      const jsonResponse = await axiosInstance.post(
        `${baseURL}/api/trail/bulk-delete-reading-content/`,
        formData
      );

      setResponseData(jsonResponse.data);
      
      // Then make another request to get the file for download
      const fileResponse = await axiosInstance.post(
        `${baseURL}/api/trail/bulk-delete-reading-content/`,
        formData
      );

      // Create a download URL from the response blob
      const url = window.URL.createObjectURL(new Blob([fileResponse.data]));
      setDownloadUrl(url);
    } catch (error) {
      console.error("Error processing file:", error);
      setResponseData({
        status: "error",
        message: error.response?.data?.message || error.message || "Bulk deletion failed! Please check your file and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "bulk-deletion-report.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (downloadUrl) {
      window.URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);
    }
    setResponseData(null);
  };

  // Helper function to render deletion details
  const renderDeletionDetails = () => {
    if (!responseData?.data?.deletion_details || responseData.data.deletion_details.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: '16px' }}>
        <Typography variant="h6" sx={{ mb: 2, color: "#4b5563", fontWeight: 500 }}>
          Deletion Details
        </Typography>
        <Box sx={{ maxHeight: '200px', overflowY: 'auto', bgcolor: '#f9fafb', borderRadius: '8px', p: 2 }}>
          {responseData.data.deletion_details.map((item, index) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                p: 2,
                bgcolor: '#fff',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" component="span" sx={{ color: '#111827' }}>
                  {item.title}
                </Typography>
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '9999px',
                    bgcolor: item.status === 'deleted' ? '#d1fae5' : '#fee2e2',
                    color: item.status === 'deleted' ? '#065f46' : '#b91c1c',
                    fontWeight: 500
                  }}
                >
                  {item.status}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: '0.875rem' }}>
                <Typography variant="body2" component="span" sx={{ color: '#6b7280' }}>
                  ID: <span style={{ fontWeight: 500, color: '#4b5563' }}>{item.id}</span>
                </Typography>
                <Typography variant="body2" component="span" sx={{ color: '#6b7280' }}>
                  Words Deleted: <span style={{ fontWeight: 500, color: '#4b5563' }}>{item.word_mappings_deleted}</span>
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </div>
    );
  };

  return (
    <StyledBox>
      <FormContainer>
        <Typography
          variant="h4"
          sx={{ textAlign: "center", mb: 4, fontWeight: 600, color: "#1f2937" }}>
          Bulk Delete Reading Content
        </Typography>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: "#4b5563" }}>
              Upload Excel File
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "#6b7280" }}>
              Upload an Excel file containing the IDs of reading content to delete. 
              Please ensure your file follows the required format.
            </Typography>
            <TextField
              type="file"
              onChange={handleFileChange}
              fullWidth
              variant="outlined"
              placeholder="Choose Excel file"
              InputProps={{
                startAdornment: (
                  <DescriptionIcon sx={{ width: "20px", height: "20px", color: "#9ca3af", mr: 1 }} />
                ),
              }}
              inputProps={{
                accept: ".xlsx, .xls"
              }}
            />
          </div>

          <StyledButton
            type="submit"
            variant="contained"
            fullWidth
            sx={{ py: 1.5, mt: 2 }}
            disabled={loading}>
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Process Deletion"
            )}
          </StyledButton>
        </form>
      </FormContainer>

      <StyledDialog open={open} onClose={!loading ? handleClose : null} maxWidth="sm" fullWidth>
        <div style={{ position: "relative" }}>
          <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" sx={{ color: "#1f2937" }}>
                {loading ? "Processing Deletion" : "Deletion Results"}
              </Typography>
              {!loading && (
                <IconButton onClick={handleClose} sx={{ color: "#9ca3af", "&:hover": { color: "#4b5563" } }}>
                  <CloseIcon />
                </IconButton>
              )}
            </div>
          </div>

          <DialogContent sx={{ p: 3 }}>
            {loading ? (
              <LoaderContainer>
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                  Processing your deletion request...
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  This may take a few moments. Please don't close this window.
                </Typography>
              </LoaderContainer>
            ) : (
              responseData && (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "16px",
                      borderRadius: "8px",
                      backgroundColor: responseData.status === "success" ? "#ecfdf5" : "#fef2f2",
                      color: responseData.status === "success" ? "#065f46" : "#b91c1c"
                    }}>
                    {responseData.status === "success" ? (
                      <CheckCircleIcon sx={{ height: "20px", width: "20px", color: "#10b981" }} />
                    ) : (
                      <ErrorIcon sx={{ height: "20px", width: "20px", color: "#ef4444" }} />
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {responseData.data?.message || responseData.message}
                    </Typography>
                  </div>

                  {responseData.status === "success" && responseData.data && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                        <Box sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                          <Typography variant="body2" color="text.secondary">Titles Processed</Typography>
                          <Typography variant="h6" sx={{ color: '#111827', fontWeight: 600 }}>
                            {responseData.data.titles_processed || 0}
                          </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                          <Typography variant="body2" color="text.secondary">Content Deleted</Typography>
                          <Typography variant="h6" sx={{ color: '#111827', fontWeight: 600 }}>
                            {responseData.data.content_deleted || 0}
                          </Typography>
                        </Box>
                      </Box>

                      {responseData.data.content_not_found && responseData.data.content_not_found.length > 0 && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#fff7ed', borderRadius: '6px', border: '1px solid #ffedd5' }}>
                          <Typography variant="subtitle2" sx={{ color: '#9a3412', mb: 1 }}>
                            Content Not Found ({responseData.data.content_not_found.length})
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {responseData.data.content_not_found.map((item, index) => (
                              <Chip 
                                key={index} 
                                label={item} 
                                size="small" 
                                sx={{ bgcolor: '#ffedd5', color: '#9a3412' }} 
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {renderDeletionDetails()}
                    </Box>
                  )}

                  {downloadUrl && (
                    <DownloadButton
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                      fullWidth
                      sx={{ mt: 3 }}>
                      Download Full Report
                    </DownloadButton>
                  )}
                </div>
              )
            )}
          </DialogContent>

          {!loading && (
            <div style={{ borderTop: "1px solid #e5e7eb", padding: "16px 24px", backgroundColor: "#f9fafb" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleClose} variant="outlined">
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </StyledDialog>
    </StyledBox>
  );
};

export default Testformdeletepage;